import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import Footer from 'tio-reimagined/components/footer';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedArray } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import type ConditionModel from 'tio-data/models/condition';
import type Owner from '@ember/owner';
import { contains } from 'tio-ui/utilities';
import Checkbox from 'tio-reimagined/components/checkbox';
import CheckboxGroup from 'tio-reimagined/components/checkbox-group';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';

export interface TuitionAssistanceFormsAcceptConditionsSignature {
  Args: {
    conditions: readonly ConditionModel[];
    onChange: (value: ConditionModel[]) => void;
    errors?: Record<string, string[] | undefined>;
    hasSubmitted?: boolean;
  };
}

export default class TuitionAssistanceFormsAcceptConditionsComponent extends Component<TuitionAssistanceFormsAcceptConditionsSignature> {
  // TODO: Not sure this is the correct type.
  @tracked conditionToView?: ConditionModel;
  acceptedTerms!: ConditionModel[];

  constructor(owner: Owner, args: TuitionAssistanceFormsAcceptConditionsSignature['Args']) {
    super(owner, args);
    this.acceptedTerms = trackedArray([]);
  }

  get isPdfCondition() {
    return this.conditionToView?.hasPdf;
  }

  didChangeAcceptanceOfTerm = (condition: ConditionModel, value?: boolean) => {
    if (value) {
      this.acceptedTerms.push(condition);
    } else {
      const index = this.acceptedTerms.indexOf(condition);
      if (index !== -1) {
        this.acceptedTerms.splice(index, 1);
      }
    }
    this.args.onChange?.([...this.acceptedTerms]);
  };

  setConditionToView = (condition: ConditionModel) => {
    this.conditionToView = condition;
  };

  close = () => {
    this.conditionToView = undefined;
  };

  <template>
    <CheckboxGroup
      data-test-id="accept-conditions-checkbox-group"
      @errors={{errorsForValibotField
        "conditionAccepted"
        schemaErrors=@errors
        hasSubmitted=@hasSubmitted
      }}
      class="tio-col-tight"
    >
      {{#each @conditions as |condition|}}
        <div class="tio-row-between w-full">
          <div class="tio-row-flush">
            <Checkbox
              data-test-id="condition-checkbox"
              @checked={{contains this.acceptedTerms condition}}
              @onChange={{fn this.didChangeAcceptanceOfTerm condition}}
              @label={{condition.prompt}}
            />
          </div>
          {{#if condition.hasPdf}}
            <a
              type="button"
              class="text-link font-medium text-sm uppercase ml-2 underline"
              href={{condition.pdfUrl}}
              target="_blank"
              rel="noopener noreferrer"
            >
              {{t "common.view"}}
            </a>
          {{else}}
            <button
              type="button"
              class="text-link font-medium text-sm uppercase ml-2 underline"
              {{on "click" (fn this.setConditionToView condition)}}
            >
              {{t "common.view"}}
            </button>
          {{/if}}
        </div>
      {{/each}}
    </CheckboxGroup>

    {{#if this.conditionToView}}
      <Modal @isOpen={{true}} @onClose={{this.close}} class="min-h-64" @size="xl">
        <:header>
          <p class="font-semibold">{{this.conditionToView.prompt}}</p>
        </:header>
        <:body>
          {{htmlSafe this.conditionToView.fullText}}
        </:body>
        <:footer>
          <Footer @isRunning={{false}} @onSubmit={{this.close}} @submitText={{t "common.close"}} />
        </:footer>
      </Modal>
    {{/if}}
  </template>
}
